import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Base API URL from environment variables
const apiUrl = process.env.REACT_APP_API_URL;

// Initial state for users
const initialState = {
  data: [], // List of users
  searchResults: null, // Search results
  selectedUser: null, // Details of a selected user
  userDetailsCache: {}, // Cache for user details to avoid redundant API calls
  isLoading: false, // Global loading state for users
  isSearching: false, // Loading state for searching
  isFetchingDetails: false, // Loading state for fetching user details
  error: null, // Global error state
  searchError: null, // Search error state
  detailsError: null, // Error state for user details
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  },
  searchPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  },
  isSearchMode: false,
  // Add filters
  filters: {
    level: "",
    status: "all", // all, active, inactive, verified, unverified
    pageSize: 10
  },
  // Add available levels from API
  availableLevels: [],
  isLoadingLevels: false
};

// Thunk: Fetch available levels for filtering
export const fetchAvailableLevels = createAsyncThunk(
  "users/fetchAvailableLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/UserLevels/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      
      console.log("API Response (Available Levels):", response.data);
      return response.data.data || response.data;
    } catch (error) {
      console.error("API Error (Available Levels):", error.response || error.message);
      return rejectWithValue(error.response?.data || "Failed to fetch levels");
    }
  }
);

// Thunk: Fetch all users with pagination and filters
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async ({ page = 1, pageSize = 20, level = "", status = "all" } = {}, { rejectWithValue }) => {
    try {
      let url = `${apiUrl}/Profile/All/?page=${page}&page_size=${pageSize}`;
      
      // Add level filter if specified
      if (level) {
        url += `&level=${encodeURIComponent(level)}`;
      }
      
      // Add status filter if specified
      if (status !== "all") {
        switch (status) {
          case "active":
            url += `&is_active=true`;
            break;
          case "inactive":
            url += `&is_active=false`;
            break;
          case "verified":
            url += `&is_verified=true`;
            break;
          case "unverified":
            url += `&is_verified=false`;
            break;
        }
      }

      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      
      console.log("API Response (All Users):", response.data);
      return {
        ...response.data,
        currentPage: page,
        pageSize: pageSize
      };
    } catch (error) {
      console.error("API Error (All Users):", error.response || error.message);
      return rejectWithValue(error.response?.data || "Failed to fetch users");
    }
  }
);

// Thunk: Search users with search type support
export const searchUsers = createAsyncThunk(
  "users/searchUsers",
  async ({ query, searchType = "username", level = "" }, { rejectWithValue }) => {
    try {
      let url = `${apiUrl}/Profile/search/?q=${query}&search_type=${searchType}`;
      
      // Add level filter to search if specified
      if (level) {
        url += `&level=${encodeURIComponent(level)}`;
      }

      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      
      console.log("API Response (Search Users):", response.data);
      return {
        ...response.data,
        isSearch: true
      };
    } catch (error) {
      console.error("API Error (Search Users):", error.response || error.message);
      return rejectWithValue(error.response?.data?.message || "Failed to search users");
    }
  }
);

// Thunk: Fetch details of a specific user by ID
export const fetchUserDetails = createAsyncThunk(
  "users/fetchUserDetails",
  async (profile_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Profile/${profile_id}/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      console.log("API Response (User Details):", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "API Error (User Details):",
        error.response || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch user details"
      );
    }
  }
);

// Users slice
const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = null;
      state.searchError = null;
      state.isSearchMode = false;
      state.searchPagination = {
        currentPage: 1,
        pageSize: state.filters.pageSize,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
      };
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
      state.detailsError = null;
    },
    clearUserDetailsCache: (state) => {
      state.userDetailsCache = {};
    },
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPageSize: (state, action) => {
      state.filters.pageSize = action.payload;
      state.pagination.pageSize = action.payload;
    },
    setCustomPageSize: (state, action) => {
      const customSize = parseInt(action.payload);
      if (customSize > 0) { // Remove the 1000 limit
        state.filters.pageSize = customSize;
        state.pagination.pageSize = customSize;
      }
    },
    clearFilters: (state) => {
      state.filters = {
        level: "",
        status: "all",
        pageSize: state.filters.pageSize // Keep the page size
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch available levels
      .addCase(fetchAvailableLevels.pending, (state) => {
        state.isLoadingLevels = true;
      })
      .addCase(fetchAvailableLevels.fulfilled, (state, action) => {
        state.isLoadingLevels = false;
        state.availableLevels = action.payload;
      })
      .addCase(fetchAvailableLevels.rejected, (state, action) => {
        state.isLoadingLevels = false;
        console.error("Failed to fetch levels:", action.payload);
      })

      // Fetch all users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || action.payload;
        state.error = null;
        state.pagination = {
          currentPage: action.payload.current_page || action.payload.currentPage,
          pageSize: action.payload.page_size || action.payload.pageSize,
          totalCount: action.payload.count,
          totalPages: action.payload.total_pages || Math.ceil(action.payload.count / action.payload.pageSize),
          hasNext: !!action.payload.has_next,
          hasPrevious: !!action.payload.has_previous
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "An error occurred while fetching users";
      })

      // Search users
      .addCase(searchUsers.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.isSearching = false;
        // Handle the response structure - search returns results array
        state.searchResults = action.payload.results || action.payload.data || action.payload;
        state.searchError = null;
        state.isSearchMode = true;
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
      })

      // ✅ fetchUserDetails.pending - مع cache checking
      .addCase(fetchUserDetails.pending, (state, action) => {
        const profileId = action.meta.arg;
        
        // ✅ تحقق من الـ cache أولاً
        if (state.userDetailsCache[profileId]) {
          console.log("✅ Using cached user details for ID:", profileId);
          state.selectedUser = state.userDetailsCache[profileId];
          state.isFetchingDetails = false;
          return; // ✅ لا تكمل - استخدم الـ cache
        }
        
        console.log("🔄 Fetching user details for ID:", profileId);
        state.isFetchingDetails = true;
        state.detailsError = null;
      })
      
      // ✅ fetchUserDetails.fulfilled - احفظ في الـ cache
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        const profileId = action.meta.arg;
        
        console.log("✅ User details fetched successfully for ID:", profileId);
        state.isFetchingDetails = false;
        
        // ✅ احفظ في الـ cache أولاً
        state.userDetailsCache[profileId] = action.payload;
        
        // ✅ ثم حدث selectedUser
        state.selectedUser = action.payload;
      })
      
      // ✅ fetchUserDetails.rejected
      .addCase(fetchUserDetails.rejected, (state, action) => {
        console.error("❌ Failed to fetch user details:", action.error);
        state.isFetchingDetails = false;
        state.detailsError = action.payload || "An error occurred while fetching user details";
        state.selectedUser = null;
      });
  },
});

export const { 
  clearSearchResults, 
  clearSelectedUser, 
  clearUserDetailsCache,
  setSearchMode, 
  setFilters, 
  setPageSize, 
  setCustomPageSize,
  clearFilters 
} = usersSlice.actions;

// Selectors
export const selectUsers = (state) => state.users.data;
export const selectSearchResults = (state) => state.users.searchResults;
export const selectSelectedUser = (state) => state.users.selectedUser;
export const selectUsersLoading = (state) => state.users.isLoading;
export const selectUsersSearching = (state) => state.users.isSearching;
export const selectUsersFetchingDetails = (state) => state.users.isFetchingDetails;
export const selectUsersError = (state) => state.users.error;
export const selectUsersSearchError = (state) => state.users.searchError;
export const selectUsersDetailsError = (state) => state.users.detailsError;
export const selectUsersPagination = (state) => state.users.pagination;
export const selectUsersSearchPagination = (state) => state.users.searchPagination;
export const selectUsersSearchMode = (state) => state.users.isSearchMode;
export const selectUsersFilters = (state) => state.users.filters;
export const selectAvailableLevels = (state) => state.users.availableLevels;
export const selectIsLoadingLevels = (state) => state.users.isLoadingLevels;

export default usersSlice.reducer;